import { useState, useEffect } from "react";
import { z } from "zod";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useWidgetState, useDeviceType, useTheme } from "./hooks/useOpenAiGlobal";
import { Partner } from "./types";

// Webhook URL for intro requests
const WEBHOOK_INTRO_URL = "https://workflows.nesha.ai/webhook/gpt-intro-request";

const emailSchema = z.string().email("Please enter a valid email address");

// Helper to extract domain from email
function getEmailDomain(email: string): string | null {
  const match = email.toLowerCase().match(/@([^@]+)$/);
  return match ? match[1] : null;
}

// Helper to normalize domain (remove www. prefix, lowercase)
function normalizeDomain(domain: string): string {
  return domain.toLowerCase().replace(/^www\./, "");
}

// Sanitize strings for webhook payload
function sanitizeString(str: string, maxLength: number = 255): string {
  return str
    .trim()
    .slice(0, maxLength)
    .replace(/[\n\r\t]/g, " ")  // Replace line breaks/tabs with spaces
    .replace(/[<>]/g, "");      // Remove angle brackets (basic XSS prevention)
}

interface PartnerCardProps {
  partner: Partner;
  variant?: "inline" | "fullscreen";
  requestorDomain?: string;
  userEmail?: string;
  onEmailSubmitted?: (email: string) => void;
}


const RequestButton = ({ className, setIsDialogOpen, isRequested }: { className?: string; setIsDialogOpen: React.Dispatch<React.SetStateAction<boolean>>; isRequested: boolean }) => (
  <button
    onClick={() => setIsDialogOpen(true)}
    disabled={isRequested}
    aria-label="Request introduction"
    className={`px-4 py-2 bg-primary text-primary-foreground text-sm font-medium rounded-lg transition-opacity hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed ${className || ""}`}
  >
    {isRequested ? "Requested" : "Request Intro"}
  </button>
);


const PartnerCard = ({
  partner,
  variant = "inline",
  requestorDomain,
  userEmail,
  onEmailSubmitted,
}: PartnerCardProps) => {
  const isFullscreen = variant === "fullscreen";
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [error, setError] = useState("");
  const [isRequested, setIsRequested] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const theme = useTheme();

  // Use SDK device detection instead of CSS breakpoints
  const deviceType = useDeviceType();
  const isMobile = deviceType === "mobile";

  // Widget state persistence (OpenAI SDK) - persists across page refreshes
  const persistedWidgetState = useWidgetState();
  const partnerId = partner.id || partner.name;

  // Initialize email from widgetState (persisted) or userEmail prop (fallback)
  const [email, setEmail] = useState(() => {
    return persistedWidgetState?.userEmail || userEmail || "";
  });

  // Recover persisted state for this specific partner
  useEffect(() => {
    if (persistedWidgetState?.submittedIntros?.[partnerId]) {
      setIsRequested(true);
    }
  }, [persistedWidgetState, partnerId]);

  // Sync email from widgetState when it changes (persists across refreshes)
  useEffect(() => {
    if (persistedWidgetState?.userEmail) {
      setEmail(persistedWidgetState.userEmail);
    } else if (userEmail) {
      setEmail(userEmail);
    }
  }, [persistedWidgetState?.userEmail, userEmail]);

  // Normalize score
  const score =
    typeof partner.score === "number"
      ? partner.score
      : partner.matchScore
        ? Math.round(partner.matchScore) / 10
        : null;

  // Normalize tags
  const tags = Array.isArray(partner.tags)
    ? partner.tags
    : partner.tags
      ? String(partner.tags).split(/[,|]/).map((tag) => tag.trim()).filter(Boolean)
      : [];

  // Normalize round text
  const roundText = partner.fundingRound
    ? `Round: ${partner.fundingRound}`
    : partner.round
      ? partner.round
      : null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    // Validate email format
    const result = emailSchema.safeParse(email);
    if (!result.success) {
      setError(result.error.issues[0].message);
      return;
    }

    // Validate email domain matches requestor domain
    if (requestorDomain) {
      const emailDomain = getEmailDomain(email);
      const normalizedRequestorDomain = normalizeDomain(requestorDomain);

      if (!emailDomain || normalizeDomain(emailDomain) !== normalizedRequestorDomain) {
        setError(`Please use your company email (@${normalizedRequestorDomain})`);
        return;
      }
    }

    setIsLoading(true);

    try {
      const response = await fetch(WEBHOOK_INTRO_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          companyName: sanitizeString(partner.name),
          companyId: sanitizeString(partnerId),
          partnerDomain: sanitizeString(partnerId),
          requestorDomain: sanitizeString(requestorDomain || "unknown"),
          userEmail: email.trim(),
          sourceThreadId: sanitizeString(partner.source_thread_id || ""),
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to submit intro request: ${response.statusText}`);
      }

      toast({
        title: "Request Sent!",
        description: `We'll connect you with ${partner.name} at ${email}.`,
      });

      setIsDialogOpen(false);
      setIsRequested(true);
      onEmailSubmitted?.(email.trim());

      // Persist to widget state (OpenAI SDK) - survives page refreshes
      const widgetState = (window as any).openai?.widgetState || {};
      const updatedState = {
        ...widgetState,
        userEmail: email.trim(), // Store globally for reuse across all partners
        submittedIntros: {
          ...(widgetState.submittedIntros || {}),
          [partnerId]: {
            email: email.trim(),
            timestamp: new Date().toISOString(),
          },
        },
      };
      await (window as any).openai?.setWidgetState?.(updatedState);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to submit request");
    } finally {
      setIsLoading(false);
    }
  };


  const websiteUrl = partner.id ? `https://${partner.id}` : partner.website || "#";

  return (
    <>
      <div
        className={`
          bg-card border border-border rounded-lg p-4 transition-all duration-200
          hover:bg-partner-hover ${theme === "dark" ? "" : "hover:shadow-md"}
          ${isFullscreen ? "flex flex-col gap-3 h-full" : ""}
        `}
      >
        {/* Inline layout - uses SDK device detection */}
        {!isFullscreen && !isMobile && (
          <div className="flex flex-row items-start justify-between gap-4">
            {/* Left content */}
            <div className="flex-1 min-w-0">
              <a
                href={websiteUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-foreground underline hover:text-primary transition-colors"
              >
                {partner.name}
              </a>

              {/* Tags */}
              {tags.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {tags.map((tag, index) => (
                    <span
                      key={index}
                      className="px-2 py-0.5 text-xs font-medium rounded-full bg-tag text-tag-foreground"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}

              {/* Description */}
              {partner.description && (
                <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                  {partner.description}
                </p>
              )}
            </div>

            {/* Right content - score badge + button */}
            <div className="flex flex-col items-end justify-between flex-shrink-0 self-stretch">
              <div className="w-11 h-11 rounded-full bg-score-badge flex items-center justify-center">
                <span className="text-sm font-semibold text-score-badge-foreground">
                  {score?.toFixed(1) ?? "--"}
                </span>
              </div>
              <RequestButton setIsDialogOpen={setIsDialogOpen} isRequested={isRequested} />
            </div>
          </div>
        )}

        {/* Mobile inline layout */}
        {!isFullscreen && isMobile && (
          <div className="flex flex-col gap-3">
            {/* Name + Badge row */}
            <div className="flex items-start justify-between">
              <a
                href={websiteUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-foreground underline hover:text-primary transition-colors"
              >
                {partner.name}
              </a>
              <div className="w-10 h-10 rounded-full bg-score-badge flex items-center justify-center flex-shrink-0">
                <span className="text-sm font-semibold text-score-badge-foreground">
                  {score?.toFixed(1) ?? "--"}
                </span>
              </div>
            </div>

            {/* Tags */}
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {tags.map((tag, index) => (
                  <span
                    key={index}
                    className="px-2 py-0.5 text-xs font-medium rounded-full bg-tag text-tag-foreground"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}

            {/* Description */}
            {partner.description && (
              <p className="text-sm text-muted-foreground line-clamp-3">
                {partner.description}
              </p>
            )}

            {/* Full-width button */}
            <RequestButton className="w-full" setIsDialogOpen={setIsDialogOpen} isRequested={isRequested} />
          </div>
        )}

        {/* Fullscreen card layout */}
        {isFullscreen && (
          <>
            {/* Top row: name + score */}
            <div className="flex items-start justify-between">
              <a
                href={websiteUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-foreground text-lg underline hover:text-primary transition-colors"
              >
                {partner.name}
              </a>
              <div className="w-11 h-11 rounded-full bg-score-badge flex items-center justify-center flex-shrink-0">
                <span className="text-sm font-bold text-score-badge-foreground">
                  {score?.toFixed(1) ?? "--"}
                </span>
              </div>
            </div>

            {/* Tags */}
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {tags.map((tag, index) => (
                  <span
                    key={index}
                    className="px-2.5 py-1 text-xs font-medium rounded-full bg-tag text-tag-foreground"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}

            {/* Description */}
            {partner.description && (
              <p className="text-sm text-muted-foreground">
                {partner.description}
              </p>
            )}

            {/* Round info */}
            {(roundText || partner.footprint) && (
              <p className="text-xs text-muted-foreground">
                {roundText}
                {roundText && partner.footprint ? " · " : ""}
                {partner.footprint || null}
              </p>
            )}

            {/* CTA row */}
            <div className="flex items-center justify-end pt-2 border-t border-border mt-auto">
              <RequestButton className="w-full sm:w-auto" setIsDialogOpen={setIsDialogOpen} isRequested={isRequested} />
            </div>
          </>
        )}
      </div>

      {/* Email Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Request Intro</DialogTitle>
            <DialogDescription>
              Enter your {requestorDomain ? "company " : ""}email address and I'll reach out to {partner.name}.
              {requestorDomain && (
                <span className="block mt-1 text-xs">
                  Please use your @{normalizeDomain(requestorDomain)} email.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="intro-email-input" className="text-sm font-medium text-foreground block mb-2">
                Email Address
              </label>
              <Input
                id="intro-email-input"
                type="email"
                placeholder={requestorDomain ? `you@${normalizeDomain(requestorDomain)}` : "your@email.com"}
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError("");
                }}
                aria-describedby={error ? "email-error" : undefined}
                className={error ? "border-destructive" : ""}
              />
              {error && <p id="email-error" className="text-sm text-destructive mt-1" role="alert" aria-live="polite">{error}</p>}
            </div>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsDialogOpen(false)}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  "Send Request"
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PartnerCard;
